import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import { apiClient } from "../services/api";
import { buttonStyle, buttonBackgroundColor } from "../components/Style";
import TableInput from "../components/TableInput";
import PageLayout from "../components/PageLayout";
import Badge from "../components/Badge";
import { Role } from "../types/User";
import {
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  clickableCellStyle,
  emptyRowStyle,
} from "../components/Style";

type UserRow = {
  id: number;
  username: string;
  role: 'admin' | 'teacher' | 'viewer';
  roles?: Role[];
  hasPhoto: boolean;
  hasGoogle: boolean;
  googleEmail?: string;
  createdAt: string;
};

const columnHelper = createColumnHelper<UserRow>();

function UsersManagementPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      const data = await apiClient.getUsers();
      setUsers(data);
    } catch (error) {
      console.error("사용자 목록 조회 실패:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const getRoleBadgeProps = useCallback((roleName: string) => {
    const propsMap: Record<string, { backgroundColor: string; textColor: string; borderColor: string }> = {
      'app_admin':        { backgroundColor: '#fee2e2', textColor: '#d73a49', borderColor: '#fca5a5' },
      'director':         { backgroundColor: '#f3e8ff', textColor: '#6f42c1', borderColor: '#d8b4fe' },
      'manager':          { backgroundColor: '#ddf4ff', textColor: '#0366d6', borderColor: '#b6e3ff' },
      'teaching_teacher': { backgroundColor: '#f0f9f4', textColor: '#1f883d', borderColor: '#a4dbb5' },
      'grading_teacher':  { backgroundColor: '#fef3c7', textColor: '#d97706', borderColor: '#fcd34d' },
      'kiosk':            { backgroundColor: '#f3f4f6', textColor: '#6a737d', borderColor: '#d1d5db' },
    };
    return propsMap[roleName] || { backgroundColor: '#f3f4f6', textColor: '#6a737d', borderColor: '#d1d5db' };
  }, []);

  // 테이블 컬럼 정의
  const columns = useMemo(() => [
    columnHelper.accessor("username", {
      header: "아이디",
      size: 150,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/users/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("roles", {
      header: "역할",
      size: 200,
      cell: (info) => {
        const roles = info.getValue();
        if (!roles || roles.length === 0) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
            {roles.map((role) => (
              <Badge
                key={role.id}
                {...getRoleBadgeProps(role.name)}
              >
                {role.display_name}
              </Badge>
            ))}
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
    }),
    columnHelper.accessor("hasPhoto", {
      header: "사진",
      size: 80,
      cell: (info) => (
        <span style={{ color: info.getValue() ? '#060' : '#999' }}>
          {info.getValue() ? '✓' : '✗'}
        </span>
      ),
      sortingFn: "basic",
    }),
    columnHelper.accessor("hasGoogle", {
      header: "Google",
      size: 80,
      cell: (info) => (
        <span style={{ color: info.getValue() ? '#060' : '#999' }}>
          {info.getValue() ? '✓' : '✗'}
        </span>
      ),
      sortingFn: "basic",
    }),
    columnHelper.accessor("googleEmail", {
      header: "이메일",
      size: 200,
      cell: (info) => info.getValue() || '-',
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("createdAt", {
      header: "생성일시",
      size: 180,
      cell: (info) => new Date(info.getValue()).toLocaleString('ko-KR', { hour12: false, timeZone: 'Asia/Seoul' }),
      sortingFn: "datetime",
    }),
  ], [navigate, getRoleBadgeProps]);

  // Table instance
  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <PageLayout
      title="사용자 관리"
      actionRows={[
        <button
          key="add-user"
          onClick={() => navigate("/users/new")}
          style={{ ...buttonStyle, ...buttonBackgroundColor.success }}
        >
          + 사용자 추가
        </button>,
      ]}
    >
      <style>
        {`
          .users-table tbody tr:hover {
            background-color: #f6f8fa;
          }
        `}
      </style>
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table className="users-table" style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  
                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(false, header.getSize(), {})}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                          
                          {/* Filter row */}
                          <div style={filterContainerStyle}>
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={emptyRowStyle}
                >
                  사용자가 없습니다.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={getTanstackTdStyle(false, cell.column.getSize())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}

export default UsersManagementPage;
